import { useState, useEffect } from 'react';
import axios from 'axios';

const VUOTO_LR = {
  nome: '', cognome: '', nato_a: '', provincia_nascita: '', nato_il: '',
  codice_fiscale: '', residente_a: '', provincia_residenza: '',
  indirizzo_residenza: '', note: '', richiesta: '', numero_richiesta: '',
};

const VUOTO_DT = {
  nome: '', cognome: '', titolo: '', nato_a: '', provincia_nascita: '', nato_il: '',
  codice_fiscale: '', residente_a: '', provincia_residenza: '',
  indirizzo_residenza: '', richiesta: '', numero_richiesta: '',
};

function fmtData(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('it-IT');
}

// ── Form generico per LR o DT ──────────────────────────────────────────────
function FormPersona({ tipo, dati, onChange, onSalva, onAnnulla, saving }) {
  const isDT = tipo === 'dt';
  return (
    <div className="bg-[#EEF4FB] rounded-xl border border-[#2E6096]/20 p-4 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Campo label="Cognome *" value={dati.cognome} onChange={v => onChange('cognome', v)} />
        <Campo label="Nome *"    value={dati.nome}    onChange={v => onChange('nome', v)} />
        {isDT && <Campo label="Titolo (es. ing., geom.)" value={dati.titolo} onChange={v => onChange('titolo', v)} />}
        <Campo label="Nato/a a"           value={dati.nato_a}            onChange={v => onChange('nato_a', v)} />
        <Campo label="Provincia nascita"  value={dati.provincia_nascita} onChange={v => onChange('provincia_nascita', v)} />
        <Campo label="Data di nascita" type="date" value={dati.nato_il ? dati.nato_il.slice(0,10) : ''} onChange={v => onChange('nato_il', v)} />
        <Campo label="Codice fiscale"     value={dati.codice_fiscale}    onChange={v => onChange('codice_fiscale', v)} />
        <Campo label="Residente a"        value={dati.residente_a}       onChange={v => onChange('residente_a', v)} />
        <Campo label="Provincia residenza" value={dati.provincia_residenza} onChange={v => onChange('provincia_residenza', v)} />
        <Campo label="Indirizzo residenza" value={dati.indirizzo_residenza} onChange={v => onChange('indirizzo_residenza', v)} />
        <Campo label="Richiesta"          value={dati.richiesta}         onChange={v => onChange('richiesta', v)} />
        <Campo label="N° richiesta"       value={dati.numero_richiesta}  onChange={v => onChange('numero_richiesta', v)} />
        {!isDT && <Campo label="Note" value={dati.note} onChange={v => onChange('note', v)} colSpan />}
      </div>
      <div className="flex gap-2 pt-1">
        <button onClick={onSalva} disabled={saving || !dati.cognome}
          className="bg-[#2E6096] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#1A3A5C] disabled:opacity-50 transition">
          {saving ? 'Salvataggio…' : 'Salva'}
        </button>
        <button onClick={onAnnulla}
          className="border border-gray-300 text-gray-600 px-4 py-2 rounded-lg text-sm hover:bg-gray-50 transition">
          Annulla
        </button>
      </div>
    </div>
  );
}

function Campo({ label, value, onChange, type = 'text', colSpan }) {
  return (
    <div className={colSpan ? 'sm:col-span-2' : ''}>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <input
        type={type}
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E6096]"
      />
    </div>
  );
}

// ── Sezione (LR o DT) ──────────────────────────────────────────────────────
function Sezione({ titolo, tipo, impresaId, endpoint, gruppo, impresa }) {
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mostraForm, setMostraForm] = useState(false);
  const [editing, setEditing] = useState(null); // id in modifica
  const [formDati, setFormDati] = useState(tipo === 'dt' ? VUOTO_DT : VUOTO_LR);
  const [saving, setSaving] = useState(false);
  const [copiaId, setCopiaId] = useState('');
  const [recordSorgente, setRecordSorgente] = useState([]);
  const [selezionati, setSelezionati] = useState([]);
  const [copiando, setCopiando] = useState(false);

  useEffect(() => {
    axios.get(endpoint)
      .then(r => setLista(r.data))
      .finally(() => setLoading(false));
  }, [endpoint]);

  useEffect(() => {
    if (!copiaId) { setRecordSorgente([]); setSelezionati([]); return; }
    const path = tipo === 'lr' ? 'legali-rappresentanti' : 'direttori-tecnici';
    axios.get(`/api/imprese/${copiaId}/${path}`)
      .then(r => { setRecordSorgente(r.data); setSelezionati([]); });
  }, [copiaId, tipo]);

  function apriNuovo() {
    setEditing(null);
    setFormDati(tipo === 'dt' ? { ...VUOTO_DT } : { ...VUOTO_LR });
    setMostraForm(true);
  }

  function apriModifica(persona) {
    setEditing(persona.id);
    setFormDati({ ...persona, nato_il: persona.nato_il ? persona.nato_il.slice(0, 10) : '' });
    setMostraForm(true);
  }

  function annulla() {
    setMostraForm(false);
    setEditing(null);
  }

  function cambio(campo, val) {
    setFormDati(prev => ({ ...prev, [campo]: val }));
  }

  async function salva() {
    setSaving(true);
    try {
      if (editing) {
        const r = await axios.put(`${endpoint}/${editing}`, formDati);
        setLista(prev => prev.map(x => x.id === editing ? r.data : x));
      } else {
        const r = await axios.post(endpoint, formDati);
        setLista(prev => [...prev, r.data]);
      }
      annulla();
    } catch {
      alert('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  }

  function toggleSelezionato(id) {
    setSelezionati(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  }

  function annullaCopia() {
    setCopiaId('');
    setRecordSorgente([]);
    setSelezionati([]);
  }

  async function copiaSelezionati() {
    if (selezionati.length === 0) return;
    setCopiando(true);
    try {
      const nuovi = [];
      for (const p of recordSorgente.filter(p => selezionati.includes(p.id))) {
        const { id: _id, impresa_id: _imp, ...dati } = p;
        const payload = {
          ...dati,
          richiesta: impresa?.richiesta ?? dati.richiesta,
          numero_richiesta: impresa?.numero_richiesta ?? dati.numero_richiesta,
        };
        const r = await axios.post(endpoint, payload);
        nuovi.push(r.data);
      }
      setLista(prev => [...prev, ...nuovi]);
      annullaCopia();
    } catch {
      alert('Errore nella copia');
    } finally {
      setCopiando(false);
    }
  }

  async function elimina(id, nome) {
    if (!window.confirm(`Eliminare ${nome}?`)) return;
    try {
      await axios.delete(`${endpoint}/${id}`);
      setLista(prev => prev.filter(x => x.id !== id));
    } catch {
      alert('Errore nell\'eliminazione');
    }
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="text-sm font-bold text-[#1A3A5C] uppercase tracking-wide">{titolo}</h2>
        <div className="flex items-center gap-2 flex-wrap">
          {gruppo && gruppo.length > 0 && !mostraForm && (
            <select value={copiaId} onChange={e => setCopiaId(e.target.value)}
              className="border border-gray-300 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[#2E6096]">
              <option value="">Copia da...</option>
              {gruppo.map(i => (
                <option key={i.id} value={i.id}>{i.richiesta || i.ragione_sociale} {i.numero_richiesta || ''}</option>
              ))}
            </select>
          )}
          {!mostraForm && (
            <button onClick={apriNuovo}
              className="bg-[#2E6096] text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-[#1A3A5C] transition">
              + Aggiungi
            </button>
          )}
        </div>
      </div>

      {copiaId && recordSorgente.length > 0 && (
        <div className="mb-4 bg-blue-50 border border-blue-200 rounded-xl p-4">
          <p className="text-xs font-semibold text-blue-700 mb-2">Seleziona i record da copiare:</p>
          <div className="space-y-1.5">
            {recordSorgente.map(p => (
              <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={selezionati.includes(p.id)} onChange={() => toggleSelezionato(p.id)} />
                <span className="text-gray-800">{p.cognome} {p.nome}{p.titolo ? ` — ${p.titolo}` : ''}</span>
                {p.codice_fiscale && <span className="text-xs text-gray-400">({p.codice_fiscale})</span>}
              </label>
            ))}
          </div>
          <div className="flex gap-2 mt-3">
            <button onClick={copiaSelezionati} disabled={copiando || selezionati.length === 0}
              className="bg-[#2E6096] text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-[#1A3A5C] disabled:opacity-50 transition">
              {copiando ? 'Copiando…' : `Copia selezionati (${selezionati.length})`}
            </button>
            <button onClick={annullaCopia}
              className="border border-gray-300 text-gray-600 px-3 py-1.5 rounded-lg text-xs hover:bg-gray-50 transition">
              Annulla
            </button>
          </div>
        </div>
      )}

      {mostraForm && (
        <div className="mb-4">
          <FormPersona
            tipo={tipo}
            dati={formDati}
            onChange={cambio}
            onSalva={salva}
            onAnnulla={annulla}
            saving={saving}
          />
        </div>
      )}

      {loading ? (
        <p className="text-sm text-gray-400">Caricamento…</p>
      ) : lista.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-4">Nessun record presente</p>
      ) : (
        <div className="space-y-3">
          {lista.map(p => (
            <div key={p.id} className="border border-gray-200 rounded-lg px-4 py-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-semibold text-gray-800 text-sm">
                    {p.cognome} {p.nome}{p.titolo ? ` — ${p.titolo}` : ''}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {[p.nato_a, p.provincia_nascita ? `(${p.provincia_nascita})` : null, p.nato_il ? fmtData(p.nato_il) : null].filter(Boolean).join(' ')}
                  </p>
                  {p.codice_fiscale && <p className="text-xs text-gray-500">C.F. {p.codice_fiscale}</p>}
                  {p.residente_a && <p className="text-xs text-gray-500">Residente a {p.residente_a} {p.provincia_residenza ? `(${p.provincia_residenza})` : ''}</p>}
                  {(p.richiesta || p.numero_richiesta) && (
                    <p className="text-xs text-gray-400 mt-1">
                      {[p.richiesta, p.numero_richiesta].filter(Boolean).join(' — ')}
                    </p>
                  )}
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button onClick={() => apriModifica(p)}
                    className="text-xs text-[#2E6096] hover:underline">
                    Modifica
                  </button>
                  <button onClick={() => elimina(p.id, `${p.cognome} ${p.nome}`)}
                    className="text-xs text-red-500 hover:underline">
                    Elimina
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Tab principale ─────────────────────────────────────────────────────────
export default function TabLRDT({ impresaId, impresa }) {
  const [gruppo, setGruppo] = useState([]);

  useEffect(() => {
    if (!impresa?.ragione_sociale) return;
    axios.get('/api/imprese')
      .then(r => {
        const altri = r.data.filter(i =>
          i.ragione_sociale.trim().toLowerCase() === impresa.ragione_sociale.trim().toLowerCase()
          && i.id !== parseInt(impresaId)
        );
        setGruppo(altri);
      });
  }, [impresa?.ragione_sociale, impresaId]);

  const base = `/api/imprese/${impresaId}`;
  return (
    <div className="space-y-6">
      <Sezione titolo="Legali Rappresentanti" tipo="lr" impresaId={impresaId} endpoint={`${base}/legali-rappresentanti`} gruppo={gruppo} impresa={impresa} />
      <Sezione titolo="Direttori Tecnici" tipo="dt" impresaId={impresaId} endpoint={`${base}/direttori-tecnici`} gruppo={gruppo} impresa={impresa} />
    </div>
  );
}
